import { Link, useSearchParams } from '@remix-run/react';
import { AuctionImages } from '~/components/auction-images';
import CustomPagination from '~/components/custom-pagination';
import { Button } from '~/components/ui/button';
import Viewer from '~/components/ui/rich-text/viewer';
import { getAuctionsList } from '~/services/api/auctions';
import { AuctionType } from '~/types';
import { format } from 'date-fns';
import { Calendar, Tag } from 'lucide-react';

interface UpcomingAuctionsProps {
  auctions: Awaited<ReturnType<typeof getAuctionsList>>;
}

export default function UpcomingAuctions({ auctions }: UpcomingAuctionsProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const limit = parseInt(searchParams.get('limit') ?? '12');
  const currentPage = auctions.page;
  const totalPages = Math.ceil(auctions.totalCount / limit);

  return (
    <>
      <div className="mb-5 mt-2 flex flex-col">
        <h1 className="text-2xl font-semibold">Upcoming Auctions</h1>
        <span className="text-sm text-slate-600">{auctions.totalCount} auctions found</span>
      </div>

      <div className="my-4 h-[1px] bg-slate-200" />

      <div className="mb-4 flex flex-col gap-y-4">
        {auctions.list.length > 0 ? (
          auctions.list.map((auction, index) => {
            const auctionImages = auction.products.filter((p) => p.images.length > 0).map((p) => p.images[0]);

            return (
              <div key={index} className="flex flex-col rounded-sm border md:flex-row">
                <div className="flex basis-1/4 items-center gap-1">
                  <AuctionImages images={auctionImages} />
                </div>

                <div className="flex basis-3/4 flex-col py-4 md:flex-row">
                  <div className="flex basis-2/3 flex-col gap-y-2 px-5 py-4">
                    <div className="flex items-center gap-x-2">
                      <Tag className="-rotate-[270deg] text-slate-500" size={16} />
                      <span className="text-xs text-slate-500">
                        {auction.auctionType === AuctionType.TIMEONLINE ? 'TimeOnline' : 'Time2Live'}
                      </span>
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold">{auction.name}</h2>
                      {/* <span className="text-sm text-slate-600">{auction.description}</span> */}
                      <Viewer content={auction.description} />
                    </div>
                  </div>

                  <div className="flex basis-1/3 flex-col gap-y-1 px-4 py-4">
                    <div className="flex items-center gap-x-2">
                      <Calendar className="text-slate-500" size={18} />
                      <span className="text-xs text-slate-500">
                        Start: {format(auction.auctionStart, 'ccc, do MMM yyyy h:mm aaa zzzz')}
                      </span>
                    </div>
                    {auction.auctionType !== AuctionType.TIME2LIVE && (
                      <div className="flex items-center gap-x-2">
                        <Calendar className="text-slate-500" size={18} />
                        <span className="text-xs text-slate-500">
                          End: {format(auction.auctionEnd, 'ccc, do MMM yyyy h:mm aaa zzzz')}
                        </span>
                      </div>
                    )}

                    <div className="mt-2 flex flex-col gap-y-2">
                      {/* <Button size="sm" variant="primary">
                        Register Now
                      </Button> */}
                      <Link to={`/auction-detail/${auction.slug}`}>
                        <Button size="sm" variant="outline" className="w-full">
                          View Collection
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <span className="text-center text-sm text-slate-500">No data found</span>
        )}
      </div>

      <CustomPagination
        limit={limit}
        totalCount={auctions.totalCount}
        currentPage={currentPage}
        totalPages={totalPages}
        onPrevPage={() => {
          searchParams.set('page', (currentPage - 1).toString());
          setSearchParams(searchParams, {
            preventScrollReset: true,
          });
        }}
        onNextPage={() => {
          searchParams.set('page', (currentPage + 1).toString());
          setSearchParams(searchParams, {
            preventScrollReset: true,
          });
        }}
        onPageChange={(page) => {
          searchParams.set('page', page);
          setSearchParams(searchParams, {
            preventScrollReset: true,
          });
        }}
        onLimitChange={(limit) => {
          searchParams.set('limit', limit);
          setSearchParams(searchParams, {
            preventScrollReset: true,
          });
        }}
      />
    </>
  );
}
